import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react"
import DataTable from "components/common/dataTable/dataTable"
import { useGetClientWorkflowsQuery } from "features/clients/detail/api"
import UserProfileSummary from "features/shared/components/userProfileSummary"
import { t } from "i18next"
import { Row } from "antd"
import { Link, useSearchParams } from "react-router-dom"
import { DateFormat, Space, Text } from "components/common"
import theme from "utils/themeVars"
import WorkFlowFilters from "./components/workflowFilters"
import { convertToReadableString, joinBytItems, localisedValue } from "utils"
import { ActionButtons, Separator, StatusTag, TitleDescription } from "components/custom"
import WorkflowDetailDrawer from "./components/workflowDetailDrawer"
import { WORKFLOW_TODOS_LISTING_PATH } from "constants/app-paths"

const WorkflowsTab = ({ client, getCount = () => {} }) => {
  const [filter, setFilter] = useState({ pageLimit: 10, page: 0, requestStatus: "in_progress" })
  const [searchParams, setSearchParams] = useSearchParams()
  const queryObj = Object.fromEntries([...searchParams])
  const { data, isFetching, isError, error, isLoading, refetch } = useGetClientWorkflowsQuery(
    {
      clientId: client?.id,
      filter,
    },
    { skip: !client?.id, refetchOnMountOrArgChange: true }
  )
  const drawerRef = useRef()
  const [workflow, setWorkflow] = useState()

  useEffect(() => {
    data?.statistics?.all && getCount(data?.statistics?.all)
  }, [data?.statistics?.all])

  useEffect(() => {
    queryObj?.requestStatus && setFilter({ ...filter, requestStatus: queryObj?.requestStatus })
  }, [queryObj?.requestStatus])

  const columns = () => [
    { title: t("ID"), dataIndex: "requestId", key: "requestId", className: "tableStartSpace" },
    { title: t("Lead ID"), dataIndex: "leadId", key: "leadId" },
    { title: t("Workflow Name"), dataIndex: "requestName", key: "requestName" },
    ...(filter?.requestStatus === "in_progress" || filter?.requestStatus === "pending"
      ? [
          { title: t("Due Date"), dataIndex: "dueDate", key: "sharedFrom" },
          { title: t("Current Stage"), dataIndex: "currentStage", key: "statusType" },
          { title: t("Stage Deadline"), dataIndex: "stageDeadline", key: "stageDeadline" },
          { title: t("Assignee"), dataIndex: "assignee", key: "assignee" },
        ]
      : []),
    ...(filter?.requestStatus === "completed"
      ? [
          { title: t("Due Date"), dataIndex: "dueDate", key: "sharedFrom" },
          { title: t("Completed Date"), dataIndex: "completedDate", key: "completedDate" },
        ]
      : []),
    ...(filter?.requestStatus === "rejected"
      ? [
          { title: t("Rejection Date"), dataIndex: "rejectionDate", key: "dueDate" },
          { title: t("Rejected Stage"), dataIndex: "rejectedStage", key: "rejectedStage" },
          { title: t("Reason"), dataIndex: "rejectedReason", key: "reason" },
        ]
      : []),
    { title: t("Actions"), dataIndex: "actions", key: "actions", fixed: "right" },
  ]

  let actionButtonList = useCallback(
    item => [
      {
        key: "1",
        label: t("View Timeline"),
        icon: "ImArrowUpRight2",
        color: theme["primary-color"],
        size: "16px",
        onClick: () => {
          drawerRef?.current?.onVisible()
          setWorkflow(item)
        },
      },
    ],
    []
  )

  const tableData = () =>
    !!data?.data?.length > 0 &&
    data?.data?.map(item => {
      return {
        key: item?.key,
        requestId: (
          <Link
            to={`${WORKFLOW_TODOS_LISTING_PATH}?templateIds=${item?.workflowId}&stageId=${item?.currentStageId}&requestIds=${item?.workflowRequestId}`}
            key={item?.id}
            className="text-link"
          >
            {item?.workflowRequestId}
          </Link>
        ),
        leadId: <Text>{item?.leadId}</Text>,
        requestName: (
          <TitleDescription
            title={item?.requestName}
            gapSpace={4}
            description={<DateFormat date={item?.requestedDate} customTooltip={t("Requested on:")} />}
          />
        ),
        rejectionDate: (
          <>
            <DateFormat date={item?.rejectedDate} dateColor={item?.isRejectedAfterDue ? theme["error-color"] : ""} />
            {!!item?.isRejectedAfterDue && (
              <Text style={{ color: theme["error-color"] }}>
                {t("Overdue by ") + item?.isRejectedAfterDue + t(" days")}
              </Text>
            )}
          </>
        ),
        rejectedStage: (
          <StatusTag
            data={{
              title: `${localisedValue({ object: item?.stageSequenceData, key: "stageName" })} (${
                item?.stageSequenceData?.stageNumber
              }/${item?.stageSequenceData?.totalTemplateStages})`,
              color: "#004d40",
            }}
          />
        ),
        rejectedReason: (
          <>
            <Text>{item?.rejectionReason}</Text>
            <TitleDescription
              title={
                <>
                  <Text type="secondaryLight">
                    {localisedValue({ key: "name", object: item?.currentStageAssignee })}&nbsp;
                  </Text>
                  <UserProfileSummary staffId={item?.currentStageAssignee?.id} />
                </>
              }
              gapSpace={4}
              description={joinBytItems(
                [
                  localisedValue({ key: "designation", object: item?.currentStageAssignee }),
                  localisedValue({ key: "city", object: item?.currentStageAssignee }),
                ],
                <Separator />
              )}
            />
          </>
        ),
        dueDate: (
          <>
            <DateFormat date={item?.dueDate} dateColor={item?.isOverdue ? theme["error-color"] : ""} />
            {!!item?.isOverdue && (
              <Text style={{ color: theme["error-color"] }}>{t("Overdue by ") + item?.isOverdue + t(" days")}</Text>
            )}
          </>
        ),
        currentStage: (
          <>
            <StatusTag
              data={{
                title: `${localisedValue({ object: item?.stageSequenceData, key: "stageName" })} (${
                  item?.stageSequenceData?.stageNumber
                }/${item?.stageSequenceData?.totalTemplateStages})`,
                color: "#004d40",
              }}
            />
          </>
        ),
        stageDeadline: (
          <>
            <DateFormat date={item?.stageDeadline} dateColor={item?.isStageOverDue ? theme["error-color"] : ""} />
            {!!item?.isStageOverDue && (
              <Text style={{ color: theme["error-color"] }}>
                {t("Overdue by ") + item?.isStageOverDue + t(" days")}
              </Text>
            )}
          </>
        ),
        assignee: (
          <>
            <TitleDescription
              title={
                <>
                  {localisedValue({ key: "name", object: item?.currentStageAssignee })}&nbsp;
                  <UserProfileSummary staffId={item?.currentStageAssignee?.id} />
                </>
              }
              gapSpace={4}
              description={joinBytItems(
                [
                  localisedValue({ key: "designation", object: item?.currentStageAssignee }),
                  localisedValue({ key: "city", object: item?.currentStageAssignee }),
                ],
                <Separator />
              )}
            />
          </>
        ),
        completedDate: (
          <>
            <DateFormat date={item?.completedDate} dateColor={item?.isCompletedAfterDue ? theme["error-color"] : ""} />
            {item?.isCompletedAfterDue ? (
              <Text style={{ color: theme["error-color"] }}>
                {t("Overdue by ") + item?.isCompletedAfterDue + t(" days")}
              </Text>
            ) : (
              ""
            )}
          </>
        ),
        actions: <ActionButtons list={actionButtonList(item)} />,
      }
    })

  const onChangePage = (page, pageSize) => {
    setFilter(prev => ({ ...prev, pageLimit: pageSize, page: page }))
  }

  const workflowCategories = useMemo(() => {
    return (
      data &&
      Object.keys(data.statistics)
        ?.map(workflow => {
          if (workflow === "all") return
          return {
            title: convertToReadableString(workflow, true),
            value: workflow,
            count: data.statistics[workflow],
          }
        })
        .filter(Boolean)
    )
  }, [data])

  return (
    <>
      <WorkFlowFilters workflowCategories={workflowCategories} filterObject={filter} onSearch={setFilter} />
      <DataTable
        pagination={{ totalCount: data?.statistics?.[filter?.requestStatus] ?? 0, pageOptions: [10, 25, 50] }}
        onChangePage={onChangePage}
        data={tableData() || []}
        columns={columns()}
        loading={isFetching}
        error={error}
        onErrorRetry={refetch}
        noUrlPush={true}
        stickyOffset={0}
        scroll={{ x: "max-content" }}
      />
      <WorkflowDetailDrawer ref={drawerRef} data={workflow} />
    </>
  )
}

export default WorkflowsTab
